import { FormSuccess } from 'Components/Form/Success';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Main as Container } from 'Components/Main';
import { Detalhes } from './Details';
import { Vinculos } from './Bonds';
import { addArea, editArea, getAreaById } from 'services/areas-service';
import { getUsersName } from 'services/users-service';
import { getAssetsShort } from 'services/assets-service';
import { getSystemsShort } from 'services/systems-service';
import { getProcessesShort } from 'services/processes-service';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { Dialog, PageHeader, Button } from 'Components/ui';
import { useQuery, useQueries } from 'react-query';
import { PageLoaderForm } from 'Components/PageLoader/Form';
import { PageHelper } from '../../../Components/PageHelper';
import styled from 'styled-components';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--color-brand)';

const TabsRow = styled.div`
  display: flex;
  background: white;
  width: fit-content;
  border-radius: 12px;
  padding: 5px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  margin-bottom: 20px;
`;
const TabBtn = styled.button`
  height: 36px;
  padding: 0 20px;
  border: none;
  border-radius: 9px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $active }) => $active ? BLUE : 'transparent'};
  color: ${({ $active }) => $active ? 'white' : MUTED};
  transition: background 0.15s, color 0.15s;
  &:hover { color: ${({ $active }) => $active ? 'white' : INK}; }
`;

const tourSteps = [
  { target: 'body', content: 'Bem-vindo ao formulário de Áreas de Negócio!', placement: 'center', disableBeacon: true },
  { target: '[data-tour="area-name"]', content: 'Digite o nome da área de negócio.', disableScrolling: true },
  { target: '[data-tour="area-criticity"]', content: 'Selecione o nível de criticidade da área.', disableScrolling: true },
  { target: '[data-tour="area-user"]', content: 'Selecione o responsável pela área.', disableScrolling: true },
  { target: '[data-tour="area-tags"]', content: 'Adicione tags para facilitar a busca.', disableScrolling: true },
  { target: '[data-tour="area-description"]', content: 'Descreva as responsabilidades desta área.', disableScrolling: true },
  { target: 'body', content: 'Na aba "Vínculos" você pode vincular processos, sistemas e ativos.', placement: 'center' },
];

export const AreasForm = () => {
  const { t } = useTranslation(['asm']);
  const selectedTenant = useSelector(useAuthTenant);
  const { id } = useParams();
  const navigate = useNavigate();
  const [idArea, setIdArea] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [finished, setFinished] = useState(false);
  const [isLoadingForm, setIsLoadingForm] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    reset,
  } = useForm({
    defaultValues: {
      name: '',
      criticity: '',
      user: '',
      tags: [],
      description: '',
      processes: [],
      systems: [],
      assets: [],
      companyKey: selectedTenant.slug,
    },
  });

  const { data: users } = useQuery(['getUsers'], async () => {
    if (!selectedTenant.slug) return [];
    const result = await getUsersName(selectedTenant.slug);
    return result?.data?.length > 0 ? result?.data : [];
  });

  const queries = useQueries([
    {
      queryKey: ['getProcesses'],
      queryFn: async () => {
        if (!selectedTenant.slug) return [];
        const result = await getProcessesShort(selectedTenant.slug);
        return result?.data?.length > 0 ? result?.data : [];
      },
    },
    {
      queryKey: ['getSystems'],
      queryFn: async () => {
        if (!selectedTenant.slug) return [];
        const result = await getSystemsShort(selectedTenant.slug);
        return result?.data?.length > 0 ? result?.data : [];
      },
    },
    {
      queryKey: ['getAssets'],
      queryFn: async () => {
        if (!selectedTenant.slug) return [];
        const result = await getAssetsShort(selectedTenant.slug);
        return result?.data?.length > 0 ? result?.data : [];
      },
    },
  ]);

  const [
    { data: processes, isLoading: processLoading },
    { data: systems, isLoading: systemLoading },
    { data: assets, isLoading: assetLoading },
  ] = queries;

  const formatProcess = p => ({ _id: p._id, name: p.name, description: p.description, user: p.user, criticity: p.criticity });
  const formatAsset   = a => ({ _id: a._id, name: a.name, description: a.description, user: a.user, criticity: a.criticity });
  const formatSystem  = s => ({ _id: s._id, name: s.name, description: s.description, criticity: s.criticity, user: s.user });

  const formatForm = dados => ({
    name: dados.name,
    criticity: dados.criticity,
    user: dados.user,
    tags: dados.tags ?? [],
    description: dados.description,
    processes: (dados.processes ?? []).map(formatProcess),
    systems: (dados.systems ?? []).map(formatSystem),
    assets: (dados.assets ?? []).map(formatAsset),
    companyKey: dados.companyKey ?? selectedTenant.slug,
  });

  const handleComplete = async (formData) => {
    try {
      setIsLoadingForm(true);
      const formattedForm = formatForm(formData);
      const res = id ? await editArea(id, formattedForm) : await addArea(formattedForm);
      if (res.status === 200 || res.status === 201) {
        setIdArea(res.data._id ?? id);
        setFinished(true);
        setIsLoadingForm(false);
      } else {
        throw new Error('Ocorreu um erro ao tentar salvar a Área');
      }
    } catch (error) {
      setIsLoadingForm(false);
      Dialog.alert({
        title: t('asm:areas.delete.errorTitle'),
        message: error.response?.data?.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const handleReset = () => {
    reset();
    setActiveTab(0);
    setFinished(false);
    setIdArea(null);
    navigate('/areas/add');
  };

  const { isLoading, error, data: area } = useQuery(['getArea', id], async () => {
    if (!id) return null;
    const resArea = await getAreaById(id);
    return resArea.data ?? null;
  });

  useEffect(() => {
    if (!id || !area) return;
    Object.keys(area).forEach(key => setValue(key, area[key]));
  }, [id, area, setValue]);

  const showForm = () => (id ? !isLoading && !error : true);

  const pageTitle = id ? t('asm:areas.form.editTitle') : t('asm:areas.form.addTitle');

  return (
    <>
      {!finished && (
        <PageHelper steps={tourSteps} tourKey="areas-form-tour" autoStart={true} showButton={true} />
      )}
      <Container>
        {isLoading && <PageLoaderForm />}
        {showForm() && !finished && (
          <form onSubmit={handleSubmit(handleComplete)} id="area-form" noValidate>
            <PageHeader
              title={pageTitle}
              segments={[
                { label: t('common:breadcrumbs.mapping') },
                { label: t('asm:areas.title'), route: '/areas' },
                { label: pageTitle },
              ]}
              actions={
                <>
                  <Button variant="outline" type="button" onClick={() => navigate('/areas')} sx={{ height: '44px', padding: '0 20px' }}>
                    {t('common:buttons.cancel')}
                  </Button>
                  <Button variant="primary" type="submit" disabled={isLoadingForm} sx={{ height: '44px', fontSize: '14px', padding: '0 20px' }}>
                    {t('asm:areas.form.save')}
                  </Button>
                </>
              }
            />

            <TabsRow>
              <TabBtn type="button" $active={activeTab === 0} onClick={() => setActiveTab(0)}>
                {t('asm:areas.show.tabDetails')}
              </TabBtn>
              <TabBtn type="button" $active={activeTab === 1} onClick={() => setActiveTab(1)}>
                {t('asm:areas.show.tabBonds')}
              </TabBtn>
            </TabsRow>

            {activeTab === 0 && (
              <Detalhes
                register={register}
                control={control}
                errors={errors}
                users={users}
                id={id}
                t={t}
              />
            )}
            {activeTab === 1 && (
              <Vinculos
                control={control}
                processes={processes}
                systems={systems}
                assets={assets}
                processLoading={processLoading}
                systemLoading={systemLoading}
                assetLoading={assetLoading}
                t={t}
              />
            )}
          </form>
        )}
        {finished && (
          <div style={{ paddingTop: 32 }}>
            <FormSuccess
              title={t('asm:areas.form.success')}
              labelStartAgain={t('asm:areas.form.addAnother')}
              handleStartAgain={handleReset}
              labelShow={t('asm:areas.form.viewDetails')}
              handleShow={() => navigate('/areas/show/' + idArea)}
            />
          </div>
        )}
      </Container>
    </>
  );
};
